import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { User, Globe, Building, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAllYfiCountries } from "@/hooks/useYfiCountries";
import 'flag-icons/css/flag-icons.min.css';
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslation } from "@/contexts/LanguageContext";
import { useCountryName } from "@/hooks/useCountryName";

interface IdentityStepProps {
  fullName: string;
  nationalOrg: string;
  localOrg: string;
  onFullNameChange: (value: string) => void;
  onNationalOrgChange: (value: string) => void;
  onLocalOrgChange: (value: string) => void;
  onNext: () => void;
}

export function IdentityStep({
  fullName,
  nationalOrg,
  localOrg,
  onFullNameChange,
  onNationalOrgChange,
  onLocalOrgChange,
  onNext,
}: IdentityStepProps) {
  const { t } = useTranslation();
  const { getCountryName } = useCountryName();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Fetch countries from database
  const { countries, countryFlagCodes, isLoading } = useAllYfiCountries();

  // Get sorted list of country names
  const jciCountries = useMemo(() => 
    countries.map(c => c.country_name).sort(),
    [countries]
  );

  const filteredCountries = useMemo(() => {
    if (!search) return jciCountries;
    return jciCountries.filter((c) =>
      c.toLowerCase().includes(search.toLowerCase()) ||
      getCountryName(c).toLowerCase().includes(search.toLowerCase())
    );
  }, [search, jciCountries, getCountryName]);

  const isValid = fullName.trim() && nationalOrg.trim();

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
      <div className="text-center space-y-2">
        <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <User className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">{t('onboarding.identity.title')}</h2>
        <p className="text-muted-foreground">{t('onboarding.identity.description')}</p>
      </div>

      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="fullName" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            {t('onboarding.identity.fullName')}
          </Label>
          <Input
            id="fullName"
            value={fullName}
            onChange={(e) => onFullNameChange(e.target.value)}
            placeholder={t('onboarding.identity.fullNamePlaceholder')}
            className="bg-background"
          />
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            {t('onboarding.identity.nationalOrg')}
          </Label>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="w-full justify-between bg-background"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common.loading')}
                  </span>
                ) : nationalOrg ? (
                  <span className="flex items-center gap-2">
                    <span className={`fi fi-${countryFlagCodes[nationalOrg] || 'un'} w-5 h-4 rounded shadow-sm`} />
                    <span>JCI {getCountryName(nationalOrg)}</span>
                  </span>
                ) : (
                  t('onboarding.identity.nationalOrgPlaceholder')
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full p-0 z-50" align="start">
              <Command>
                <CommandInput
                  placeholder={t('onboarding.identity.searchCountry')}
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList>
                  <CommandEmpty>{t('onboarding.identity.noCountryFound')}</CommandEmpty>
                  <CommandGroup className="max-h-64 overflow-auto">
                    {filteredCountries.map((country) => {
                      const localizedName = getCountryName(country);
                      const showEnglish = localizedName !== country;
                      return (
                        <CommandItem
                          key={country}
                          value={country}
                          onSelect={() => {
                            onNationalOrgChange(country);
                            setOpen(false);
                            setSearch("");
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              nationalOrg === country ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <span className={`fi fi-${countryFlagCodes[country] || 'un'} w-5 h-4 rounded shadow-sm mr-2`} />
                          <span>JCI {localizedName}</span>
                          {showEnglish && (
                            <span className="ml-1 text-muted-foreground">({country})</span>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label htmlFor="localOrg" className="flex items-center gap-2">
            <Building className="w-4 h-4" />
            {t('onboarding.identity.localOrg')} <span className="text-muted-foreground text-sm">({t('onboarding.identity.optional')})</span>
          </Label>
          <Input
            id="localOrg"
            value={localOrg}
            onChange={(e) => onLocalOrgChange(e.target.value)}
            placeholder={t('onboarding.identity.localOrgPlaceholder')}
            className="bg-background"
          />
        </div>
      </div>

      <Button
        onClick={onNext}
        disabled={!isValid || isLoading}
        className="w-full"
        size="lg"
      >
        {t('onboarding.continue')}
      </Button>
    </motion.div>
  );
}
